import React, { createContext, useContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid'; // For generating temporary UUIDs
import {
    saveLead,
    fetchLeadById,
    getLeadsByUser,
    updateLead,
    deleteLead
} from 'apis/leadCaptureApi'; // Import necessary API functions
import { useError } from './ErrorContext';

const LeadCaptureContext = createContext();

// Initial values structure for lead capture
const initialLeadCaptureValues = {
    leadName: '',
    leadEmail: '',
    leadPhoneNumber: '',
    leadJobTitle: '',
    leadCompany: '',
    leadNotes: '',
    customFields: {},
    leadSource: '',
    userId: '',
    cardId: '',
    leadId: '', // Store the MongoDB ID or temporary UUID
    formHeader: 'Share your info',  // UI value: form header
    connectButtonLabel: 'Connect',  // UI value: connect button label
    formDisclaimer: 'Touch does not sell or share your data.',  // UI value: form disclaimer
};

export const LeadCaptureProvider = ({ children }) => {
    const [leadCaptureState, setLeadCaptureState] = useState(initialLeadCaptureValues);
    const [contacts, setContacts] = useState([]);
    const [isLoadingContacts, setIsLoadingContacts] = useState(false);
    const [contactsError, setContactsError] = useState(null);
    const [error, setError] = useState(null);

    // Get error context for global error handling
    const { addError, handleApiCall } = useError();

    // Enhanced error handling for lead capture operations
    const handleLeadCaptureError = (error, operation = 'lead-capture-operation') => {
        console.error('Lead capture operation error:', error);

        // Only show user-relevant errors, log technical errors
        if (error.message?.includes('validation') ||
            error.message?.includes('permission') ||
            error.message?.includes('session') ||
            error.message?.includes('Failed to fetch') ||
            error.message?.includes('Error saving') ||
            error.response?.status === 401 ||
            error.response?.status === 403) {
            addError(error, {
                source: 'lead-capture',
                type: operation,
                retryable: true,
                autoDismiss: false
            });
        }

        setError(error.message || 'An error occurred while processing lead capture');
    };

    // Clear error after timeout
    React.useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Function to update lead capture data with error handling
    const updateLeadCaptureData = (newData) => {
        try {
            setLeadCaptureState((prevState) => ({
                ...prevState,
                ...newData,
            }));
            setError(null); // Clear any existing errors on successful update
        } catch (error) {
            handleLeadCaptureError(error, 'lead-capture-update');
        }
    };

    // Initialize lead creation by setting a temporary ID
    const initializeLeadCreation = () => {
        try {
            const newTempLeadId = uuidv4();
            updateLeadCaptureData({ leadId: newTempLeadId }); // Set leadId to the temporary UUID
        } catch (error) {
            handleLeadCaptureError(error, 'lead-capture-initialization');
        }
    };

    // Function to select an existing lead from API and update state with global error handling
    const selectLead = async (leadId) => {
        try {
            setError(null);

            // Use global error handling for API call
            const leadData = await handleApiCall(
                () => fetchLeadById(leadId),
                {
                    source: 'lead-capture-select',
                    retryable: true,
                    autoDismiss: true
                }
            );

            updateLeadCaptureData({ ...leadData, leadId }); // Set lead data and ID in state
        } catch (error) {
            console.error('Failed to fetch lead:', error);
            handleLeadCaptureError(error, 'lead-capture-select');
            throw new Error('Failed to fetch lead data');
        }
    };

    // Function to save lead and update leadId with the MongoDB ID with global error handling
    const saveLeadAndUpdateId = async () => {
        try {
            setError(null);

            // Use global error handling for API call
            const mongoDBId = await handleApiCall(
                () => saveLead({ ...leadCaptureState }),
                {
                    source: 'lead-capture-save',
                    retryable: true,
                    autoDismiss: false
                }
            );

            // Update the leadId in the state with the actual MongoDB ID
            updateLeadCaptureData({ leadId: mongoDBId }); // Replace temporary ID with MongoDB ID
        } catch (error) {
            console.error('Failed to save lead and update ID:', error);
            handleLeadCaptureError(error, 'lead-capture-save');
            throw new Error('Error saving lead data');
        }
    };

    // ----------------
    // Contacts Management Functions
    // ----------------

    // Fetch contacts for the user with global error handling
    const fetchContacts = useCallback(async (userId) => {
        if (!userId) {
            console.warn('Cannot fetch contacts: userId is undefined');
            return;
        }

        console.log('Fetching contacts for user:', userId);
        setIsLoadingContacts(true);
        setContactsError(null);
        setError(null);

        try {
            // Use global error handling for API call
            const fetchedContacts = await handleApiCall(
                () => getLeadsByUser(userId),
                {
                    source: 'lead-capture-contacts-fetch',
                    retryable: true,
                    autoDismiss: true
                }
            );

            console.log('Fetched contacts:', fetchedContacts);

            // Check if fetched contacts is an array
            if (Array.isArray(fetchedContacts)) {
                setContacts(fetchedContacts);
            } else if (fetchedContacts && fetchedContacts.leads && Array.isArray(fetchedContacts.leads)) {
                // Some APIs return { leads: [...] }
                setContacts(fetchedContacts.leads);
            } else {
                console.error('Unexpected response format:', fetchedContacts);
                setContacts([]);
                const formatError = new Error('Invalid response format from API');
                handleLeadCaptureError(formatError, 'lead-capture-contacts-format');
            }
        } catch (error) {
            console.error('Error fetching contacts:', error);
            setContactsError('Failed to load contacts. Please try again.');
            setContacts([]);
            handleLeadCaptureError(error, 'lead-capture-contacts-fetch');
        } finally {
            setIsLoadingContacts(false);
            console.log('Finished loading contacts');
        }
    }, [handleApiCall, addError]);

    // Update a contact in the contacts array with error handling
    const updateContact = (updatedContact) => {
        try {
            if (!updatedContact || !updatedContact._id) {
                throw new Error('Invalid contact data for update');
            }

            setContacts(prevContacts =>
                prevContacts.map(contact =>
                    contact._id === updatedContact._id ? updatedContact : contact
                )
            );
        } catch (error) {
            handleLeadCaptureError(error, 'lead-capture-contact-update');
        }
    };

    // Add a new contact to the contacts array with error handling
    const addContact = (newContact) => {
        try {
            if (!newContact) {
                throw new Error('Invalid contact data for addition');
            }

            console.log('🔍 LeadCaptureContext: Adding contact to state:', newContact);
            console.log('🔍 LeadCaptureContext: Current contacts count:', contacts.length);

            setContacts(prevContacts => {
                const updatedContacts = [newContact, ...prevContacts];
                console.log('🔍 LeadCaptureContext: Updated contacts count:', updatedContacts.length);
                console.log('🔍 LeadCaptureContext: First contact in list:', updatedContacts[0]);
                return updatedContacts;
            });
        } catch (error) {
            handleLeadCaptureError(error, 'lead-capture-contact-add');
        }
    };

    // Delete a contact from the contacts array with error handling
    const deleteContact = (contactId) => {
        try {
            if (!contactId) {
                throw new Error('Contact ID is required for deletion');
            }

            setContacts(prevContacts =>
                prevContacts.filter(contact => contact._id !== contactId)
            );
        } catch (error) {
            handleLeadCaptureError(error, 'lead-capture-contact-delete');
        }
    };

    // Function to clear error
    const clearError = () => {
        setError(null);
        setContactsError(null);
    };

    // Expose state and handlers through context
    return (
        <LeadCaptureContext.Provider
            value={{
                leadCaptureState,
                error,
                initializeLeadCreation,
                selectLead,
                saveLeadAndUpdateId,
                updateLeadCaptureData, // expose for fine-grained updates
                clearError,

                // Individual setters for form fields with error handling
                setLeadName: (name) => {
                    try {
                        if (typeof name !== 'string') {
                            throw new Error('Lead name must be a string');
                        }
                        updateLeadCaptureData({ leadName: name });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-name-update');
                    }
                },
                setLeadEmail: (email) => {
                    try {
                        if (typeof email !== 'string') {
                            throw new Error('Lead email must be a string');
                        }
                        // Basic email validation
                        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                            throw new Error('Invalid email format');
                        }
                        updateLeadCaptureData({ leadEmail: email });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-email-update');
                    }
                },
                setLeadPhoneNumber: (phoneNumber) => {
                    try {
                        if (typeof phoneNumber !== 'string') {
                            throw new Error('Lead phone number must be a string');
                        }
                        updateLeadCaptureData({ leadPhoneNumber: phoneNumber });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-phone-update');
                    }
                },
                setLeadJobTitle: (jobTitle) => {
                    try {
                        if (typeof jobTitle !== 'string') {
                            throw new Error('Lead job title must be a string');
                        }
                        updateLeadCaptureData({ leadJobTitle: jobTitle });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-job-title-update');
                    }
                },
                setLeadCompany: (company) => {
                    try {
                        if (typeof company !== 'string') {
                            throw new Error('Lead company must be a string');
                        }
                        updateLeadCaptureData({ leadCompany: company });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-company-update');
                    }
                },
                setLeadNotes: (notes) => {
                    try {
                        if (typeof notes !== 'string') {
                            throw new Error('Lead notes must be a string');
                        }
                        updateLeadCaptureData({ leadNotes: notes });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-notes-update');
                    }
                },
                setCustomFields: (fields) => {
                    try {
                        if (typeof fields !== 'object' || fields === null) {
                            throw new Error('Custom fields must be an object');
                        }
                        updateLeadCaptureData({ customFields: fields });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-custom-fields-update');
                    }
                },
                setLeadSource: (source) => {
                    try {
                        if (typeof source !== 'string') {
                            throw new Error('Lead source must be a string');
                        }
                        updateLeadCaptureData({ leadSource: source });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-source-update');
                    }
                },
                setUserId: (userId) => {
                    try {
                        if (typeof userId !== 'string') {
                            throw new Error('User ID must be a string');
                        }
                        updateLeadCaptureData({ userId });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-user-id-update');
                    }
                },
                setCardId: (cardId) => {
                    try {
                        if (typeof cardId !== 'string') {
                            throw new Error('Card ID must be a string');
                        }
                        updateLeadCaptureData({ cardId });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-card-id-update');
                    }
                },

                // UI-specific setters with error handling
                setFormHeader: (header) => {
                    try {
                        if (typeof header !== 'string') {
                            throw new Error('Form header must be a string');
                        }
                        updateLeadCaptureData({ formHeader: header });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-form-header-update');
                    }
                },
                setConnectButtonLabel: (label) => {
                    try {
                        if (typeof label !== 'string') {
                            throw new Error('Connect button label must be a string');
                        }
                        updateLeadCaptureData({ connectButtonLabel: label });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-button-label-update');
                    }
                },
                setFormDisclaimer: (disclaimer) => {
                    try {
                        if (typeof disclaimer !== 'string') {
                            throw new Error('Form disclaimer must be a string');
                        }
                        updateLeadCaptureData({ formDisclaimer: disclaimer });
                    } catch (error) {
                        handleLeadCaptureError(error, 'lead-capture-disclaimer-update');
                    }
                },

                // Contacts management
                contacts,
                isLoadingContacts,
                contactsError,
                fetchContacts,
                updateContact,
                addContact,
                deleteContact
            }}
        >
            {children}
        </LeadCaptureContext.Provider>
    );
};

// Custom hook to use the LeadCaptureContext
export const useLeadCaptureContext = () => {
    const context = useContext(LeadCaptureContext);
    if (!context) {
        throw new Error('useLeadCaptureContext must be used within a LeadCaptureProvider');
    }
    return context;
};